import { Callback } from "game-common/models"
import { Container, Graphics, InteractionEvent } from "pixi.js"

import { CloseButton } from "./close_button"
import { disposeTextTextures } from "../text/dispose_text"
import { getUiMetrics } from "./ui_metrics"

export interface DialogOptions {
    padding?: number
    x?: number
    y?: number
    // Content-box dimensions. Padding is added once to form the outer frame.
    // Omitted dimensions are measured from content; explicit ones do not grow with children.
    width?: number
    height?: number
    content?: Graphics
    borderColor?: number
    backgroundColor?: number
    backgroundAlpha?: number
    closeCallback?: Function
    onCloseClick?: Callback
    onMouseOver?: Function
    onMouseOut?: Function
    center?: boolean
    plain?: boolean
    unescapeable?: boolean
    // Where to hang the circular close button, in CONTENT coordinates (i.e. the same space the
    // content graphic draws in, before the dialog's padding offset). Pass the top-right corner of the
    // panel the content actually paints when that panel is inset from the dialog rect -- a full-screen
    // modal that only draws a right-docked panel, say -- so the button straddles the panel's corner
    // instead of the rect's. Defaults to just inside the dialog's own top-right corner.
    closeAnchor?: { x: number; y: number } | (() => { x: number; y: number })
    closeButtonScale?: number
    // Hide the visual dismiss control without disabling Escape or programmatic close.
    hideCloseButton?: boolean | (() => boolean)
    // Drag surface behind the content, so interactive widgets above it own their gestures.
    // A callback keeps its size current when the panel content is rebuilt.
    dragHandle?: { x: number; y: number; width: number; height: number }
        | (() => { x: number; y: number; width: number; height: number })
    dragBounds?: () => { left: number; right: number; top: number; bottom: number }
    onDrag?: () => void
}

export class Dialog {
    parent: Container
    padding: number
    x: number
    y: number
    width: number
    height?: number
    content: Graphics
    backgroundColor: number
    borderColor: number
    backgroundAlpha: number
    private dialog: Graphics
    private closeCallback?: Function
    private onCloseClick?: Callback
    private onMouseOver?: Function
    private onMouseOut?: Function
    private closed: boolean = false
    private plain: boolean
    private unescapeable: boolean
    private closeAnchor?: DialogOptions["closeAnchor"]
    private closeButton?: CloseButton
    private closeButtonScale: number
    private hideCloseButton?: DialogOptions["hideCloseButton"]
    private dragOptions: Pick<DialogOptions, "dragHandle" | "dragBounds" | "onDrag">

    constructor(
        parent: Container,
        {
            padding = 0,
            x = 0,
            y = 0,
            content = new Graphics(),
            borderColor = 0xffffff,
            backgroundColor = 0x000000,
            backgroundAlpha = 0.91,
            width,
            height,
            closeCallback,
            onCloseClick,
            onMouseOver,
            onMouseOut,
            plain,
            unescapeable,
            closeAnchor,
            closeButtonScale = 1,
            hideCloseButton,
            dragHandle,
            dragBounds,
            onDrag,
        }: DialogOptions,
    ) {
        this.parent = parent
        this.padding = padding
        this.content = content
        this.x = x
        this.y = y
        this.backgroundAlpha = backgroundAlpha
        this.backgroundColor = backgroundColor
        this.width = width
        this.height = height
        this.closeCallback = closeCallback
        this.onCloseClick = onCloseClick
        this.borderColor = borderColor
        this.onMouseOver = onMouseOver
        this.onMouseOut = onMouseOut
        this.plain = plain
        this.unescapeable = unescapeable
        this.closeAnchor = closeAnchor
        this.closeButtonScale = closeButtonScale
        this.hideCloseButton = hideCloseButton
        this.dragOptions = { dragHandle, dragBounds, onDrag }
    }

    update = () => {
        if (this.closed) {
            return
        }
        if (this.dialog) {
            this.layout()
            return
        }
        const dialog = new Graphics()
        this.dialog = dialog
        this.dialog.zIndex = 3
        this.parent.addChild(dialog)

        this.content.x = this.padding
        this.content.y = this.padding

        this.attachDragHandle(dialog)
        dialog.addChild(this.content)

        if (!this.unescapeable) {
            const close = new CloseButton({ onClick: this.onCloseClick })
            close.scale.set(this.closeButtonScale)
            this.closeButton = close
            dialog.addChild(close)
        }
        this.layout()

        dialog.interactive = !!this.onMouseOut || !!this.onMouseOver
        if (this.onMouseOver) {
            dialog.on("mouseover", () => this.onMouseOver())
        }
        if (this.onMouseOut) {
            dialog.on("mouseout", () => this.onMouseOut())
        }
    }

    // Resize the existing frame without rebuilding content or registering input listeners again.
    layout = (options?: Pick<DialogOptions, "x" | "y" | "width" | "height">) => {
        if (this.closed || !this.dialog) {
            return
        }
        if (options) {
            Object.assign(this, options)
        }
        const bounds = this.content.getLocalBounds()
        const width = (this.width ?? Math.max(0, bounds.x + bounds.width)) + this.padding * 2
        const height = (this.height ?? Math.max(0, bounds.y + bounds.height)) + this.padding * 2
        const dialog = this.dialog
        dialog.position.set(this.x, this.y)
        dialog.clear()
        if (!this.plain) {
            dialog.lineStyle(1, this.borderColor, 0.5)
        }
        dialog.beginFill(this.backgroundColor, this.backgroundAlpha)
        if (this.plain) {
            dialog.drawRect(0, 0, width, height)
        } else {
            dialog.drawRoundedRect(0, 0, width, height, 8)
        }
        dialog.endFill()
        if (this.closeButton) {
            this.closeButton.visible = !(typeof this.hideCloseButton === "function" ? this.hideCloseButton() : this.hideCloseButton)
            this.closeButton.refreshMetrics()
            const anchor = typeof this.closeAnchor === "function" ? this.closeAnchor() : this.closeAnchor
            const radius = this.closeButton.getRadius() * this.closeButtonScale
            this.closeButton.position.set(
                anchor ? anchor.x + this.padding : width - radius - 6,
                anchor ? anchor.y + this.padding : radius + 6,
            )
            // A target expanded for touch must stay reachable inside the visible, safe frame.
            const metrics = getUiMetrics()
            if (metrics.touch) {
                const target = this.closeButton.getHitSize()
                const halfW = target.width * this.closeButtonScale / 2
                const halfH = target.height * this.closeButtonScale / 2
                const safe = metrics.safeViewport
                const clamp = (value: number, min: number, max: number) => max < min ? (min + max) / 2 : Math.max(min, Math.min(max, value))
                this.closeButton.x = clamp(this.closeButton.x, Math.max(0, safe.left - this.x) + halfW,
                    Math.min(width, safe.right - this.x) - halfW)
                this.closeButton.y = clamp(this.closeButton.y, Math.max(0, safe.top - this.y) + halfH,
                    Math.min(height, safe.bottom - this.y) - halfH)
            }
        }
    }

    private attachDragHandle = (dialog: Graphics) => {
        const { dragHandle, dragBounds, onDrag } = this.dragOptions
        if (!dragHandle) {
            return
        }
        const handle = new Graphics()
        handle.hitArea = {
            contains: (x: number, y: number) => {
                const rect = typeof dragHandle === "function" ? dragHandle() : dragHandle
                const left = rect.x + this.padding
                const top = rect.y + this.padding
                return x >= left && y >= top && x < left + rect.width && y < top + rect.height
            },
        }
        handle.interactive = true
        handle.cursor = "grab"
        dialog.addChild(handle)

        let grab: { x: number; y: number; pointerId: number } | undefined
        handle.on("pointerdown", (event: InteractionEvent) => {
            // Pixi normalizes an active TouchEvent to button=1. Only filter secondary mouse/pen buttons.
            if (grab || (event.data.pointerType !== "touch" && event.data.button > 0)) {
                return
            }
            const pointer = event.data.getLocalPosition(this.parent)
            grab = { x: dialog.x - pointer.x, y: dialog.y - pointer.y, pointerId: event.data.pointerId }
            handle.cursor = "grabbing"
            event.stopPropagation()
        })
        handle.on("pointermove", (event: InteractionEvent) => {
            if (!grab || event.data.pointerId !== grab.pointerId) {
                return
            }
            const pointer = event.data.getLocalPosition(this.parent)
            const bounds = dragBounds?.()
            const x = pointer.x + grab.x
            const y = pointer.y + grab.y
            dialog.x = bounds ? Math.max(bounds.left, Math.min(x, bounds.right)) : x
            dialog.y = bounds ? Math.max(bounds.top, Math.min(y, bounds.bottom)) : y
            onDrag?.()
            event.stopPropagation()
        })
        const release = (event: InteractionEvent) => {
            if (grab && event.data.pointerId === grab.pointerId) {
                grab = undefined
                handle.cursor = "grab"
            }
        }
        handle.on("pointerup", release)
        handle.on("pointerupoutside", release)
        handle.on("pointercancel", release)
    }

    close = () => {
        if (this.closed) {
            return
        }
        // Mark closed before removing children or calling user code, either of which can reenter.
        this.closed = true
        if (!this.dialog) {
            return
        }
        // Free the dialog content's per-instance Text textures on close -- every modal/dialog
        // (inventory, character sheet, etc.) builds Text via TextRenderer, and detaching without
        // this strands each one's canvas-backed texture (not reclaimed by GC). Shared item-sprite
        // textures are left intact by disposeTextTextures.
        disposeTextTextures(this.dialog)
        this.dialog.parent?.removeChild(this.dialog)
        // The content root belongs to the caller (fullscreen screens reuse it on their next open).
        this.dialog.removeChild(this.content)
        this.dialog.destroy({ children: true })
        this.closeCallback?.()
    }
}
